import React, { useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ActionBar from '../../../components/common/ActionBar';
import BrandHeader from '../../../components/common/BrandHeader';
import ClinicianCard from '../../../components/common/ClinicianCard';
import ContactHelpText from '../../../components/common/ContactHelpText';
import DetailRow from '../../../components/common/DetailRow';
import Note from '../../../components/common/Note';
import RescheduleEntry from '../../../components/common/RescheduleEntry';
import {
  Card,
  CardBody,
  PageHead,
  PageShell,
  PageSubtitle,
  PageTitle,
  SectionTitle,
} from '../../../components/common/Card';
import StepBar from '../../../components/common/StepBar';
import CancelModal from '../../LandingPage/CancelModal';
import {
  PAYMENT_TYPE,
  PRE_APPT_COUNTER_PREFIX,
  PRE_APPT_STEPS,
  PRE_APPT_STEP_NUMBER,
} from '../../../constants/preApptConstants';
import {
  ROUTE_PREFIX_PRE_APPT,
  ROUTES_PRE_APPT,
} from '../../../constants/routes';
import {
  formatAmountFromCents,
  formatAppointmentDate,
  formatAppointmentTime,
  formatAppointmentTypeLabel,
} from '../../../utils/appointmentDisplayUtils';
import { CancelLink, CancelPrompt } from './styles';

const PreApptLandingPage = ({ workflow }) => {
  const history = useHistory();
  const { workflowId } = useParams();
  const { specialist, appointment, payment } = workflow;
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const appointmentDate = formatAppointmentDate(appointment?.startDateTime);
  const appointmentTime = formatAppointmentTime(appointment?.startDateTime);
  const appointmentTypeLabel = formatAppointmentTypeLabel(
    appointment?.appointmentType
  );
  const estimatedFee = formatAmountFromCents(payment?.paymentInfo?.fee);
  const isAppointmentBooked = Boolean(appointment?.isBooked);

  const handleConfirm = () => {
    const nextRoute =
      payment?.paymentInfo?.type === PAYMENT_TYPE.AUTO
        ? ROUTES_PRE_APPT.PAYMENT_CONFIRMATION
        : ROUTES_PRE_APPT.FINISH_PAYMENT;

    history.push(`${ROUTE_PREFIX_PRE_APPT}/${workflowId}${nextRoute}`);
  };

  const toggleCancelModal = useCallback(
    (isOpen) => setIsCancelModalOpen(isOpen),
    []
  );

  return (
    <>
      <BrandHeader />
      <PageShell>
        <StepBar
          steps={PRE_APPT_STEPS}
          currentStepNumber={PRE_APPT_STEP_NUMBER.APPOINTMENT}
          counterPrefix={PRE_APPT_COUNTER_PREFIX}
        />

        <PageHead>
          <PageTitle>Confirm your appointment</PageTitle>
          <PageSubtitle>
            Please check these details are correct before you continue.
          </PageSubtitle>
        </PageHead>

        <Card>
          <CardBody>
            <ClinicianCard specialist={specialist} />
          </CardBody>
        </Card>

        {!isAppointmentBooked && (
          <Note tone="warning">
            <b>We are still confirming your appointment time.</b> Our team will
            be in touch to confirm it. You can complete your payment now to
            secure your place.
          </Note>
        )}

        <Card>
          <CardBody>
            <SectionTitle>Appointment</SectionTitle>
            {appointmentDate && (
              <DetailRow label="Date" value={appointmentDate} />
            )}
            {appointmentTime && (
              <DetailRow label="Time" value={appointmentTime} />
            )}
            {appointmentTypeLabel && (
              <DetailRow label="Type" value={appointmentTypeLabel} />
            )}
            {estimatedFee && (
              <DetailRow
                label="Estimated fee"
                value={estimatedFee}
                currency="AUD"
              />
            )}
          </CardBody>
        </Card>

        {isAppointmentBooked && (
          <Note>
            This takes about a minute. Check the appointment above is right,
            then pay for it.
          </Note>
        )}

        <RescheduleEntry workflow={workflow} />

        <CancelPrompt>
          Booked in error?{' '}
          <CancelLink type="button" onClick={() => toggleCancelModal(true)}>
            Cancel this appointment
          </CancelLink>
        </CancelPrompt>

        <ContactHelpText />

        <CancelModal
          isCancelModalOpen={isCancelModalOpen}
          toggleCancelModal={toggleCancelModal}
          pageType="PRE-APPT"
        />
      </PageShell>

      <ActionBar
        amountLabel="Estimated fee"
        amount={estimatedFee}
        currency="AUD"
        nextLabel="Confirm and continue to payment"
        onNext={handleConfirm}
      />
    </>
  );
};

export default PreApptLandingPage;
